import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  GRIEVANCE_STATUSES,
  GrievanceLogbookItem,
  GrievanceMilestone,
  GrievancePhase,
  GrievancePhaseDataApply,
} from '../../../../services/types/Grievance';

import { Button } from '../../../../shared/components';
import GenericMilestonesRow from './GenericMilestonesRow';
import ApplicabilityMilestoneRow from './ApplicabilityMilestonRow';
import Context from '../context/DetailsContext';
import ClosedMilestoneRow from './ClosedMilestoneRow';
import LogbookItemsGrid from './LogbookItemsGrid';

interface PhaseRow {
  phase: GrievancePhase;
  registrationDate: string;
  onPhaseApply: (grievancePhase: GrievancePhase, dataApply: GrievancePhaseDataApply) => void;
  onRemoveComment: (commentId: number, messageSuccess?: string) => void;
  onSaveLogbookItem: (logbookItem: GrievanceLogbookItem, messageSuccess?: string) => void;
  isReadOnly: boolean;
}

const GrievanceMilestoneListRow = (props: PhaseRow) => {
  const { phase, registrationDate, onPhaseApply, onRemoveComment, onSaveLogbookItem, isReadOnly } =
    props;
  const { grievanceNumber, grievancePhaseData, reloadPhases } = useContext(Context);

  const [rowExpanded, setRowExpanded] = useState(
    grievancePhaseData.currentPhaseName === phase.phase
  );
  const { t } = useTranslation();

  const currentPhase = useMemo(
    () => grievancePhaseData.currentPhaseName,
    [grievancePhaseData.currentPhaseName]
  );

  const isEditable = useMemo(
    () =>
      currentPhase === phase.phase &&
      grievancePhaseData.currentStatus !== GRIEVANCE_STATUSES.CLOSED &&
      !isReadOnly,
    [currentPhase, phase.phase, grievancePhaseData.currentStatus, isReadOnly]
  );

  const chevron = rowExpanded ? 'chevron-down' : 'chevron-right';

  const validateMilestones = (values: GrievancePhase): FormikErrors<GrievancePhase> => {
    if (grievancePhaseData.currentStatus !== GRIEVANCE_STATUSES.IN_PROGRESS && !isReadOnly) {
      return {};
    }

    const errors: FormikErrors<GrievancePhase> = {};

    if (values.milestones && Array.isArray(values.milestones)) {
      const milestonesErrors: FormikErrors<GrievanceMilestone>[] = [];

      values.milestones.forEach((milestone: GrievanceMilestone, index: number) => {
        const milestoneError: FormikErrors<GrievanceMilestone> = {};
        const milestoneName = phase.milestones[index].name || `Milestone ${index}`;

        // Validación 1: Fecha no puede ser anterior a la fecha de registro
        if (milestone.date) {
          const selectedDate = new Date(milestone.date);
          const regDate = new Date(registrationDate);

          if (selectedDate < regDate) {
            milestoneError.date = t('grievanceMilestoneValidations.minDate', { milestoneName });
          }

          // Validación 2: si este milestone tiene fecha, todos los milestones anteriores que sean
          // requeridos también deben tener fecha
          for (let i = 0; i < index; i += 1) {
            if (values.milestones[i].isRequired && !values.milestones[i].date) {
              const previousMilestoneName = phase.milestones[i].name || `Milestone ${i}`;
              if (!milestonesErrors[i]) {
                milestonesErrors[i] = {};
              }
              milestonesErrors[i].date = t('grievanceMilestoneValidations.requiredPrevious', {
                milestoneName: previousMilestoneName,
              });
            }
          }
        }

        if (Object.keys(milestoneError).length > 0) {
          milestonesErrors[index] = milestoneError;
        }
      });

      if (milestonesErrors.length > 0) {
        errors.milestones = milestonesErrors;
      }
    }

    return errors;
  };

  useEffect(() => {
    setRowExpanded(grievancePhaseData.currentPhaseName === phase.phase);
  }, [currentPhase]);

  return (
    <div className={`grievance-phases-list-row py-3 ${rowExpanded ? 'active' : ''}`}>
      <Button
        className="btn-tertiary py-0 px-1"
        icon={chevron}
        text={t(`grievanceStages.${phase.phase}`, phase.phase)}
        type="button"
        onClick={() => setRowExpanded((current) => !current)}
      />
      {rowExpanded && (
        <div className="grv-phases-content">
          {(() => {
            switch (phase.phase) {
              case 'Applicability':
                return (
                  <ApplicabilityMilestoneRow
                    isEditable={isEditable}
                    phase={phase}
                    registrationDate={registrationDate}
                    onPhaseApply={onPhaseApply}
                    validate={validateMilestones}
                  />
                );
              case 'Closed':
                return (
                  <ClosedMilestoneRow
                    isEditable={isEditable}
                    phase={phase}
                    registrationDate={registrationDate}
                    onPhaseApply={onPhaseApply}
                    validate={validateMilestones}
                  />
                );
              default:
                return (
                  <GenericMilestonesRow
                    isEditable={isEditable}
                    phase={phase}
                    registrationDate={registrationDate}
                    onPhaseApply={onPhaseApply}
                    validate={validateMilestones}
                  />
                );
            }
          })()}
          {phase.logbookItems && phase.logbookItems.length > 0 && (
            <LogbookItemsGrid
              grievanceNumber={grievanceNumber}
              logbookItems={phase.logbookItems}
              onRemoveComment={onRemoveComment}
              onSaveLogbookItem={onSaveLogbookItem}
              onSaveIsFinalFlagCallback={reloadPhases}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default GrievanceMilestoneListRow;
